/**
 * useCurrencyFormat.js
 *
 * Centralized helper for Brazilian Real (BRL) currency formatting.
 * Provides:
 * - formatBRL(value): Formats a number for display (e.g., 150000 → "150.000,00")
 * - rawToDisplay(cents): Converts raw cents integer to formatted display string
 * - handleCurrencyKeydown(event, currentCents): "Cents-first" input handler
 * - parseBRL(formatted): Parses formatted string back to number
 */

/**
 * Format a numeric value to Brazilian Real display format.
 * @param {number|string} value - The numeric value to format (e.g., 150000, 1234.56)
 * @returns {string} Formatted string (e.g., "150.000,00", "1.234,56")
 */
export function formatBRL(value) {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '0,00';

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Convert a raw integer (cents) to formatted display string.
 * @param {number} cents - Integer representing value in cents (e.g., 12345 = R$ 123,45)
 * @returns {string} Formatted string (e.g., "123,45")
 */
export function rawToDisplay(cents) {
  const num = (cents || 0) / 100;
  return formatBRL(num);
}

/**
 * Convert a numeric value (float) to raw cents integer.
 * @param {number} value - Float value (e.g., 1234.56)
 * @returns {number} Integer cents (e.g., 123456)
 */
export function valueToCents(value) {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return 0;
  return Math.round(num * 100);
}

/**
 * Convert raw cents integer back to float value.
 * @param {number} cents - Integer cents (e.g., 123456)
 * @returns {number} Float value (e.g., 1234.56)
 */
export function centsToValue(cents) {
  return (cents || 0) / 100;
}

/**
 * Handle keydown event for "cents-first" currency input.
 * Builds the value from right-to-left as the user types digits.
 *
 * Example sequence:
 *   Type '1' → 0,01
 *   Type '2' → 0,12
 *   Type '3' → 1,23
 *   Type '4' → 12,34
 *   Backspace → 1,23
 *
 * @param {KeyboardEvent} event - The keydown event
 * @param {number} currentCents - Current value in cents (integer)
 * @returns {{ cents: number, display: string, value: number } | null} New state or null if key was not handled
 */
export function handleCurrencyKeydown(event, currentCents) {
  const { key } = event;

  // Allow navigation keys, tab, etc.
  if (
    [
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ].includes(key)
  ) {
    return null;
  }

  // Prevent default for all other keys (we handle everything manually)
  event.preventDefault();

  let newCents = currentCents || 0;

  if (key === 'Backspace') {
    // Remove last digit: divide by 10 and floor
    newCents = Math.floor(newCents / 10);
  } else if (key === 'Delete') {
    // Clear everything
    newCents = 0;
  } else if (/^[0-9]$/.test(key)) {
    // Add digit: multiply by 10 and add new digit
    // Safety limit: prevent values larger than 999,999,999.99 (99999999999 cents)
    if (newCents <= 9999999999) {
      newCents = newCents * 10 + parseInt(key, 10);
    }
  } else {
    // Ignore other keys (including comma, period, letters, etc.)
    return null;
  }

  return {
    cents: newCents,
    display: rawToDisplay(newCents),
    value: centsToValue(newCents),
  };
}

/**
 * Parse a BRL-formatted string back to a float number.
 * Handles formats like "120.000,50" → 120000.50
 * @param {string} formatted - The formatted string
 * @returns {number} The parsed numeric value
 */
export function parseBRL(formatted) {
  if (typeof formatted === 'number') return formatted;
  if (!formatted || typeof formatted !== 'string') return 0;

  // Remove everything except digits and comma
  const cleaned = formatted
    .replace(/[R$\s]/g, '') // Remove R$, spaces
    .replace(/\./g, '') // Remove thousand separators (dots)
    .replace(',', '.'); // Replace decimal comma with dot

  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}
